import { lazy, Suspense, type ReactNode } from "react";
import ScrollToTop from "@/components/layout/ScrollToTop";
import { FullPagePublicRouteSkeleton } from "@/components/skeletons";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { StudioProvider } from "@/contexts/StudioContext";
import { WebSocketProvider } from "@/contexts/WebSocketContext";
import AppLayout from "@/components/layout/AppLayout";
import PrivateRoute from "@/components/auth/PrivateRoute";

const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const VerifyEmailChange = lazy(() => import("./pages/VerifyEmailChange"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Chats = lazy(() => import("./pages/Chats"));
const StudioData = lazy(() => import("./pages/StudioData"));
const AgentRunTracePage = lazy(() => import("./pages/AgentRunTracePage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const Archive = lazy(() => import("./pages/Archive"));
const SetupNotComplete = lazy(() => import("./pages/SetupNotComplete"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const OAuthCallbackHandler = lazy(() => import("./components/onboarding/OAuthCallbackHandler"));
const WhatsAppSignupPage = lazy(() => import("./pages/WhatsAppSignupPage"));
const CustomerIdentityVerification = lazy(() => import("./pages/civ/CustomerIdentityVerification"));

/** Full-page fallback for lazy routes outside AppLayout. */
function FullPageRouteFallback() {
  return <FullPagePublicRouteSkeleton />;
}

function SuspenseFullPage({ children }: { children: ReactNode }) {
  return <Suspense fallback={<FullPageRouteFallback />}>{children}</Suspense>;
}

/** Wrap lazy public routes with the full-page Suspense fallback. */
function publicRoute(element: ReactNode): ReactNode {
  return <SuspenseFullPage>{element}</SuspenseFullPage>;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /** Conservative: dashboard and similar views should not thrash on tab focus. */
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function toSingleSlashPath(path: string): string {
  return path.replace(/\/{2,}/g, "/");
}

function resolveManagementTarget(pathname: string, search: string, hash: string): string | null {
  const envBase = import.meta.env.VITE_MANAGEMENT_URL?.trim();
  const canonicalPathname = pathname
    .replace(/^\/mamagement(?=\/|$)/, "/management")
    .replace(/^\/managemetn(?=\/|$)/, "/management");
  const normalizedPath = canonicalPathname.startsWith("/management") ? canonicalPathname : `/management${canonicalPathname}`;

  if (envBase && envBase.length > 0) {
    const target = new URL(envBase, window.location.origin);
    const relativePath = normalizedPath.replace(/^\/management\/?/, "");
    const basePath = target.pathname.endsWith("/") ? target.pathname : `${target.pathname}/`;
    target.pathname = toSingleSlashPath(`${basePath}${relativePath}`);
    target.search = search;
    target.hash = hash;
    return target.toString();
  }

  const hostIsLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const currentPort = window.location.port;
  if (hostIsLocal && (currentPort === "5000" || currentPort === "5001" || currentPort === "5173")) {
    return `${window.location.protocol}//${window.location.hostname}:5002${normalizedPath}${search}${hash}`;
  }

  return null;
}

function ManagementRouteRedirect() {
  const location = useLocation();
  const target = resolveManagementTarget(location.pathname, location.search, location.hash);

  if (target && typeof window !== "undefined" && window.location.href !== target) {
    window.location.replace(target);
    return null;
  }

  return publicRoute(<NotFound />);
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider delayDuration={400}>
      <AuthProvider>
        <StudioProvider>
          <WebSocketProvider>
          <Toaster />
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* Public auth routes (lazy + full-page Suspense — outside AppLayout) */}
              <Route path="/login" element={publicRoute(<Login />)} />
              <Route path="/register" element={publicRoute(<Register />)} />
              <Route path="/forgot-password" element={publicRoute(<ForgotPassword />)} />
              <Route path="/reset-password" element={publicRoute(<ResetPassword />)} />
              <Route path="/accept-invitation" element={publicRoute(<AcceptInvitation />)} />
              <Route path="/verify-email" element={publicRoute(<VerifyEmail />)} />
              <Route path="/verify-email-change" element={publicRoute(<VerifyEmailChange />)} />
              <Route path="/onboarding" element={publicRoute(<Onboarding />)} />
              <Route path="/setup-not-complete" element={publicRoute(<SetupNotComplete />)} />

              {/* OAuth callback routes for onboarding */}
              <Route
                path="/onboarding/instagram/callback"
                element={publicRoute(<OAuthCallbackHandler platform="instagram" />)}
              />
              <Route
                path="/onboarding/whatsapp/callback"
                element={publicRoute(<OAuthCallbackHandler platform="whatsapp" />)}
              />

              {/* WhatsApp Embedded Signup — reached via backend startUrl redirect */}
              <Route path="/whatsapp-signup" element={publicRoute(<WhatsAppSignupPage />)} />

              {/* Management app lane lives on a separate dev port/service; hand off /management deep-links. */}
              <Route path="/management/*" element={<ManagementRouteRedirect />} />
              <Route path="/mamagement/*" element={<ManagementRouteRedirect />} />
              <Route path="/managemetn/*" element={<ManagementRouteRedirect />} />

              {/* Customer Identity Verification (CIV) — public landing under /c/* (W-Customer-Identity-Verification-Frontend) */}
              <Route path="/c/expired" element={publicRoute(<CustomerIdentityVerification forcedState="expired" />)} />
              <Route path="/c/invalid" element={publicRoute(<CustomerIdentityVerification forcedState="invalid" />)} />
              <Route path="/c/used" element={publicRoute(<CustomerIdentityVerification forcedState="used" />)} />
              <Route path="/c/:token" element={publicRoute(<CustomerIdentityVerification />)} />

              {/* Authenticated routes with layout */}
              <Route element={<PrivateRoute />}>
                <Route element={<AppLayout />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/chats/:conversationId?" element={<Chats />} />
                  <Route path="/archive" element={<Archive />} />
                  <Route path="/studio-data" element={<StudioData />} />
                  <Route path="/studio-data/agent-trace" element={<AgentRunTracePage />} />
                  <Route path="/property-data" element={<StudioData />} />
                  <Route path="/property-data/agent-trace" element={<AgentRunTracePage />} />
                  <Route path="/ai-settings" element={<Navigate to="/settings?tab=ai-behavior" replace />} />
                  <Route path="/settings" element={<SettingsPage />} />
                </Route>
              </Route>

              <Route path="*" element={publicRoute(<NotFound />)} />
            </Routes>
          </BrowserRouter>
          </WebSocketProvider>
        </StudioProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
